import React, { useContext, createContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AppContext = createContext({});

interface NavbarSubmenuType {
    visible: boolean
    item: null | string
    position: null | number
    elements: null | any[]
}

const AppProvider = ({ children }: { children: JSX.Element }) => {
    const [sidebarFull, setSidebarFull] = useState(true);
    const [showSidebar, setShowSidebar] = useState(false);

    const [darkMode, setDarkMode] = useState(false);

    // Add authentication state
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return !!localStorage.getItem('token');
    });

    const [user, setUser] = useState<any>(() => {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    });

    // True while we're checking if the stored token is still valid on app load
    const [isAuthChecking, setIsAuthChecking] = useState(() => !!localStorage.getItem('token'));

    const toggleDarkMode = () => setDarkMode(prev => !prev);
    // Add view states
    const [activeView, setActiveView] = useState<string>('Home');   // Add view state
    const [navbarSubmenu, setNavbarSubmenu] = useState<NavbarSubmenuType>({
        item: null,
        visible: false,
        position: null,
        elements: null
    })

    // Initialize authentication state on app load
    useEffect(() => {
        setIsAuthenticated(!!localStorage.getItem('token'));
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    // Validate token on app load when user has a stored token (invalid token → api interceptor clears session and redirects to login)
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setIsAuthChecking(false);
            return;
        }

        api.get('/v1/platform/auth/profile')
            .then((res) => {
                if (res.data) {
                    setUser(res.data);
                    localStorage.setItem('user', JSON.stringify(res.data));
                }
            })
            .catch(() => {
                // Invalid token: api interceptor clears storage and redirects to /login
            })
            .finally(() => {
                setIsAuthChecking(false);
            });
    }, []);

    // Authentication functions
    // Authentication functions
    const login = (token: string) => {
        localStorage.setItem('token', token);
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
        setIsAuthenticated(true);
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login'; // Redirect to login page
    };

    // View management functions
    const switchView = (view: string) => {
        setActiveView(view);
    };
    const displaySidebar = () => setShowSidebar(true);
    const hideSidebar = () => setShowSidebar(false);

    const showNavbarSubmenu = (position: number, item: string, elements: any[]) => {
        setNavbarSubmenu({
            visible: true,
            item,
            position,
            elements,
        })
    }

    const hideNavbarSubmenu = () => {
        setNavbarSubmenu({
            visible: false,
            position: null,
            item: null,
            elements: null,
        })
    }

    const toggleSidebar = () => {
        setSidebarFull(prev => !prev);
    }

    return (
        <AppContext.Provider value={{
            navbarSubmenu, showNavbarSubmenu, hideNavbarSubmenu,
            sidebarFull, toggleSidebar,
            showSidebar,
            displaySidebar,
            hideSidebar,
            darkMode,
            toggleDarkMode,
            activeView,
            switchView,
            isAuthenticated,
            isAuthChecking,
            user,
            login,
            logout,
        }}>
            {children}
        </AppContext.Provider>
    )
}

export default AppProvider

interface GlobalState {
    showSidebar: boolean
    darkMode: boolean
    toggleDarkMode: () => void
    displaySidebar: () => void
    hideSidebar: () => void
    navbarSubmenu: NavbarSubmenuType
    sidebarFull: boolean;
    showNavbarSubmenu: (position: number, item: string, elements: any[]) => void
    hideNavbarSubmenu: () => void;
    toggleSidebar: () => void;
    activeView: string
    switchView: (view: string) => void
    isAuthenticated: boolean
    isAuthChecking: boolean
    user: any
    login: (token: string) => void
    logout: () => void
}

export const useGlobalState = () => {
    return useContext(AppContext) as GlobalState;
}